import React, { useState, useEffect } from 'react';
import { Card, List, Button, Space, Typography, message, Modal, Tag, Tooltip } from 'antd';
import { 
  LaptopOutlined, 
  MobileOutlined, 
  TabletOutlined, 
  ExclamationCircleOutlined,
  LogoutOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import userApi from 'api/userApi';
import { pickData } from 'api/http';
import { getSessionInfo } from 'utils/sessionUtils';

const { Title, Text } = Typography;
const { confirm } = Modal;

/**
 * 디바이스 타입에 따른 아이콘 반환
 */
const getDeviceIcon = (deviceName) => {
  if (!deviceName) return <LaptopOutlined />;
  
  const lowerName = deviceName.toLowerCase();
  if (lowerName.includes('mobile') || lowerName.includes('android') || lowerName.includes('iphone')) {
    return <MobileOutlined />;
  }
  if (lowerName.includes('ipad') || lowerName.includes('tablet')) {
    return <TabletOutlined />;
  }
  return <LaptopOutlined />;
};

/**
 * 시간 포맷팅
 */
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // 초 단위 차이

    if (diff < 60) return '방금 전';
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}일 전`;
    
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    return '-';
  }
};

/**
 * 만료 일자 포맷팅
 */
const formatExpiryDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return '-';
  }
};

/**
 * 활성 세션 관리 컴포넌트
 */
const ActiveSessionsManager = ({ user }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentSessionId = getSessionInfo().sessionId;

  // 세션 목록 조회
  const fetchSessions = async () => {
    if (!user?.userId) return;
    
    setLoading(true);
    try {
      const response = await userApi.getActiveSessions(user.userId, currentSessionId);
      const data = pickData(response);
      setSessions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('세션 목록 조회 실패:', error);
      message.error('세션 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [user?.userId]);

  // 특정 세션 로그아웃
  const handleLogoutSession = (sessionId, deviceName) => {
    confirm({
      title: '세션 로그아웃',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>다음 세션에서 로그아웃하시겠습니까?</p>
          <p><strong>{deviceName || '알 수 없는 디바이스'}</strong></p>
        </div>
      ),
      okText: '로그아웃',
      cancelText: '취소',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await userApi.logoutSession(user.userId, sessionId);
          message.success('해당 세션이 로그아웃되었습니다.');
          fetchSessions();
        } catch (error) {
          console.error('세션 로그아웃 실패:', error);
          message.error('세션 로그아웃에 실패했습니다.');
        }
      }
    });
  };

  // 모든 세션 로그아웃
  const handleLogoutAllSessions = () => {
    const otherSessionsCount = sessions.filter(s => !s.isCurrentSession).length;
    
    if (otherSessionsCount === 0) {
      message.info('로그아웃할 다른 세션이 없습니다.');
      return;
    }

    confirm({
      title: '모든 세션 로그아웃',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>현재 세션을 제외한 <strong>{otherSessionsCount}개</strong>의 세션에서 모두 로그아웃하시겠습니까?</p>
          <p style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '8px' }}>
            다른 기기에서 다시 로그인해야 합니다.
          </p>
        </div>
      ),
      okText: '모두 로그아웃',
      cancelText: '취소',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await userApi.logoutAllSessions(user.userId, currentSessionId);
          message.success('다른 모든 세션이 로그아웃되었습니다.');
          fetchSessions();
        } catch (error) {
          console.error('전체 세션 로그아웃 실패:', error);
          message.error('세션 로그아웃에 실패했습니다.');
        }
      }
    });
  };

  return (
    <Card 
      title={
        <Space>
          <LaptopOutlined />
          <span>활성 세션 관리</span>
        </Space>
      }
      extra={
        sessions.filter(s => !s.isCurrentSession).length > 0 && (
          <Button 
            danger 
            size="small" 
            onClick={handleLogoutAllSessions}
          >
            다른 세션 모두 로그아웃
          </Button>
        )
      }
    >
      <div style={{ marginBottom: '16px' }}>
        <Text type="secondary">
          현재 로그인된 모든 기기 목록입니다. 의심스러운 세션은 즉시 로그아웃하세요.
        </Text>
      </div>

      <List
        loading={loading}
        dataSource={sessions}
        locale={{ emptyText: '활성 세션이 없습니다.' }}
        renderItem={(session) => (
          <List.Item
            key={session.sessionId || Math.random()}
            style={{
              backgroundColor: session.isCurrentSession ? '#f6ffed' : 'transparent',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '8px',
              border: session.isCurrentSession ? '1px solid #b7eb8f' : '1px solid #f0f0f0'
            }}
            actions={[
              session.isCurrentSession ? (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                  현재 세션
                </Tag>
              ) : (
                <Tooltip title="이 세션 로그아웃">
                  <Button
                    danger
                    size="small"
                    icon={<LogoutOutlined />}
                    onClick={() => handleLogoutSession(session.sessionId, session.deviceName)}
                  >
                    로그아웃
                  </Button>
                </Tooltip>
              )
            ]}
          >
            <List.Item.Meta
              avatar={
                <div style={{ fontSize: '32px', color: session.isCurrentSession ? '#52c41a' : '#1890ff' }}>
                  {getDeviceIcon(session.deviceName)}
                </div>
              }
              title={
                <Space>
                  <Text strong>{session.deviceName || '알 수 없는 디바이스'}</Text>
                  {session.isCurrentSession && (
                    <Tag color="green">현재</Tag>
                  )}
                </Space>
              }
              description={
                <Space direction="vertical" size={2}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    마지막 활동: {formatDateTime(session.createdAt)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    만료: {formatExpiryDate(session.expiresAt)}
                  </Text>
                  {session.sessionId && (
                    <Text type="secondary" style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                      세션 ID: {session.sessionId.substring(0, 8)}...
                    </Text>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />

      <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
        <Space direction="vertical" size={4}>
          <Text style={{ fontSize: '12px', color: '#595959' }}>
            💡 <strong>팁:</strong>
          </Text>
          <Text style={{ fontSize: '12px', color: '#595959' }}>
            • 본인이 사용하지 않는 세션이 보이면 즉시 로그아웃하세요.
          </Text>
          <Text style={{ fontSize: '12px', color: '#595959' }}>
            • 세션은 14일 후 자동으로 만료됩니다.
          </Text>
          <Text style={{ fontSize: '12px', color: '#595959' }}>
            • "다른 세션 모두 로그아웃"을 사용하면 현재 기기를 제외한 모든 기기에서 로그아웃됩니다.
          </Text>
        </Space>
      </div>
    </Card>
  );
};

export default ActiveSessionsManager;
